import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaChevronCircleRight } from "react-icons/fa";
import { MdFilterList, MdClose } from "react-icons/md";
import { RiFileListLine } from "react-icons/ri";
import './Statements.css';

const Statements = () => {
  const navigate = useNavigate();
  const [selectedStatus, setSelectedStatus] = useState('All');
  const [selectedRows, setSelectedRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const itemsPerPage = 10;

  // Filter states
  const [filterMonth, setFilterMonth] = useState('March 2026');
  const [filterStartDate, setFilterStartDate] = useState('2026-03-01');
  const [filterEndDate, setFilterEndDate] = useState('2026-03-31');
  const [filterAccount, setFilterAccount] = useState('All');
  const [filterStatus, setFilterStatus] = useState('All');
  const [sortBy, setSortBy] = useState('Invoice Id');
  const [sortDirection, setSortDirection] = useState('Ascending');

  // Empty array for data (Add your data here to see the table)
  const [statementsData] = useState([]);

  const calculateGrandTotal = () => {
    return filteredData.reduce((sum, item) => sum + item.total, 0);
  };

  const calculatePaymentDue = () => {
    return filteredData.reduce((sum, item) => sum + item.paymentDue, 0);
  };

  const filteredData = statementsData.filter(item => {
    const matchesStatus = selectedStatus === 'All' || item.status === selectedStatus;
    const matchesFilterStatus = filterStatus === 'All' || item.status === filterStatus;
    const matchesAccount = filterAccount === 'All' || item.customer === filterAccount;

    const matchesDate = () => {
      if (!filterStartDate && !filterEndDate) return true;
      const itemDate = new Date(item.date);
      const start = filterStartDate ? new Date(filterStartDate) : null;
      const end = filterEndDate ? new Date(filterEndDate) : null;

      if (start && end) return itemDate >= start && itemDate <= end;
      if (start) return itemDate >= start;
      if (end) return itemDate <= end;
      return true;
    };

    return matchesStatus && matchesFilterStatus && matchesAccount && matchesDate();
  });

  const sortedData = [...filteredData].sort((a, b) => {
    let aValue, bValue;
    switch(sortBy) {
      case 'Invoice Id':
        aValue = a.invoiceNumber; bValue = b.invoiceNumber; break;
      case 'Date':
        aValue = a.date; bValue = b.date; break;
      case 'Total':
        aValue = a.total; bValue = b.total; break;
      default:
        aValue = a.invoiceNumber; bValue = b.invoiceNumber;
    }
    return sortDirection === 'Ascending' ? (aValue > bValue ? 1 : -1) : (aValue < bValue ? 1 : -1);
  });

  const totalPages = Math.ceil(sortedData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentData = sortedData.slice(startIndex, endIndex);

  const handleStatusFilter = (status) => {
    setSelectedStatus(status);
    setCurrentPage(1);
  };

  const handleSelectRow = (id) => {
    if (selectedRows.includes(id)) {
      setSelectedRows(selectedRows.filter(rowId => rowId !== id));
    } else {
      setSelectedRows([...selectedRows, id]);
    }
  };

  const handleSelectAll = () => {
    if (selectedRows.length === currentData.length) {
      setSelectedRows([]);
    } else {
      setSelectedRows(currentData.map(item => item.id));
    }
  };

  const handleUpdateSelected = () => {
    if (selectedRows.length === 0) {
      alert('Please select at least one item to update');
      return;
    }
    alert(`Updated ${selectedRows.length} items successfully!`);
    setSelectedRows([]);
  };

  const openFilterModal = () => setShowFilterModal(true);
  const closeFilterModal = () => setShowFilterModal(false);
  const handleApplyFilters = () => {
    setCurrentPage(1);
    setShowFilterModal(false);
  };

  const handleResetFilters = () => {
    setFilterMonth('March 2026');
    setFilterStartDate('2026-03-01');
    setFilterEndDate('2026-03-31');
    setFilterAccount('All');
    setFilterStatus('All');
    setSortBy('Invoice Id');
    setSortDirection('Ascending');
    setSelectedStatus('All');
    setCurrentPage(1);
    setShowFilterModal(false);
  };

  const handleInvoiceClick = (invoiceNumber) => {
    navigate(`/statement-details/${invoiceNumber}`);
  };

  return (
    <div className="statements-container">
      <div className="statements-content">

        {/* HEADER ROW: Left (Title) | Right (Status + Cards) */}
        <div className="statements-header-row">

          {/* Left Side: Title only */}
          <div className="header-left-group">
            <div className="statements-header-title">
              <div className="icon-wrapper">
                <RiFileListLine className="header-icon" />
              </div>
              <h2 className="statements-title">Statements</h2>
            </div>
          </div>

          {/* Right Side: Status Dropdown + Summary Cards */}
          <div className="header-right-group">

            {/* Status Dropdown */}
            <div className="status-filter-wrapper">
              <select
                className="filter-select"
                value={selectedStatus}
                onChange={(e) => handleStatusFilter(e.target.value)}
              >
                <option value="All">Select Status</option>
                <option value="Pending">Pending</option>
                <option value="Paid">Paid</option>
                <option value="Processing">Processing</option>
                <option value="Completed">Completed</option>
              </select>
            </div>

            {/* Summary Cards */}
            <div className="header-summary-cards">
              <div className="summary-card">
                <div className="card-month">MARCH 2026</div>
                <div className="card-amount">Grand Total: £{calculateGrandTotal().toLocaleString()}</div>
              </div>

              <div className="summary-card">
                <div className="card-month">NEXT MONTH</div>
                <div className="card-amount">Payment Due: £{calculatePaymentDue().toLocaleString()}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Filters Action Section */}
        <div className="filters-section">
          <div className="filters-right">
            <div className="filters-left-group">
              <button className="update-selected-btn-left" onClick={handleUpdateSelected}>
                Update Selected ({selectedRows.length})
              </button>
            </div>

            <button className="filter-modal-btn-right" onClick={openFilterModal}>
              <MdFilterList /> Filter
            </button>
          </div>
        </div>

        {/* Filter Modal */}
        {showFilterModal && (
          <div className="filter-modal-overlay" onClick={closeFilterModal}>
            <div className="filter-modal statements-filter-modal" onClick={(e) => e.stopPropagation()}>
              <div className="filter-modal-header">
                <h2>Change Filters</h2>
                <button className="close-modal-btn" onClick={closeFilterModal}>
                  <MdClose />
                </button>
              </div>

              <div className="filter-modal-content">
                <div className="current-filters-section">
                  <h3>Current Filters:</h3>
                  <div className="current-filters-list">
                    <div className="current-filter-item">Month: {filterMonth}</div>
                    <div className="current-filter-item">Date Range: {filterStartDate} to {filterEndDate}</div>
                    <div className="current-filter-item">Account: {filterAccount}</div>
                  </div>
                </div>

                <div className="filter-section">
                  <h4>BY MONTH</h4>
                  <div className="filter-option">
                    <span className="filter-option-label">March 2026</span>
                    <div className="filter-option-sub">
                      <span>All Invoice</span>
                      <span>All Accounts</span>
                    </div>
                  </div>
                </div>

                <div className="filter-field-row">
                  <label>START DATE</label>
                  <input type="date" value={filterStartDate} onChange={(e) => setFilterStartDate(e.target.value)} />
                </div>

                <div className="filter-field-row">
                  <label>END DATE</label>
                  <input type="date" value={filterEndDate} onChange={(e) => setFilterEndDate(e.target.value)} />
                </div>

                <div className="filter-field-row">
                  <label>STATUS</label>
                  <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                    <option value="All">All</option>
                    <option value="Pending">Pending</option>
                    <option value="Paid">Paid</option>
                    <option value="Processing">Processing</option>
                    <option value="Completed">Completed</option>
                  </select>
                </div>

                <div className="filter-field-row">
                  <label>SORT BY</label>
                  <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                    <option value="Invoice Id">Invoice Id</option>
                    <option value="Date">Date</option>
                    <option value="Total">Total</option>
                  </select>
                </div>

                <div className="filter-field-row">
                  <label>SORT DIRECTION</label>
                  <select value={sortDirection} onChange={(e) => setSortDirection(e.target.value)}>
                    <option value="Ascending">Ascending</option>
                    <option value="Descending">Descending</option>
                  </select>
                </div>
              </div>

              <div className="filter-modal-footer">
                <button className="reset-filter-btn" onClick={handleResetFilters}>Reset</button>
                <button className="cancel-filter-btn" onClick={closeFilterModal}>Cancel</button>
                <button className="apply-filter-btn" onClick={handleApplyFilters}>Apply</button>
              </div>
            </div>
          </div>
        )}

        {/* Table Info */}
        <div className="table-info">
          Showing {sortedData.length === 0 ? 0 : startIndex + 1}-{Math.min(endIndex, sortedData.length)} of {sortedData.length} entries
        </div>

        {/* Statements Table */}
        <div className="statements-table-container">
          <table className="statements-table">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    checked={selectedRows.length === currentData.length && currentData.length > 0}
                    onChange={handleSelectAll}
                  />
                </th>
                <th>Date</th>
                <th>Invoice Number</th>
                <th>Order Number</th>
                <th>Customer</th>
                <th>Status</th>
                <th>Status Update</th>
                <th>Total</th>
                <th className="payment-due-header">Payment Due</th>
              </tr>
            </thead>
            <tbody>
              {currentData.length > 0 ? (
                currentData.map((item) => (
                  <tr key={item.id} className={selectedRows.includes(item.id) ? 'selected' : ''}>
                    <td style={{ textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        checked={selectedRows.includes(item.id)}
                        onChange={() => handleSelectRow(item.id)}
                      />
                    </td>
                    <td>{item.date}</td>
                    <td>{item.invoiceNumber}</td>
                    <td>{item.orderNumber}</td>
                    <td>{item.customer}</td>
                    <td>
                      <span className={`status-badge ${item.status.toLowerCase()}`}>
                        {item.status}
                      </span>
                    </td>
                    <td>{item.statusUpdate}</td>
                    <td>£{item.total.toFixed(2)}</td>
                    <td className="payment-due-cell" onClick={() => handleInvoiceClick(item.invoiceNumber)} style={{ cursor: 'pointer' }}>
                      £{item.paymentDue.toFixed(2)}
                      <FaChevronCircleRight className="payment-due-icon-small" />
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="9" className="no-data-cell">No statements found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="pagination">
            <button className="page-btn" onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))} disabled={currentPage === 1}>Previous</button>
            {[...Array(totalPages)].map((_, index) => (
              <button key={index} className={`page-btn ${currentPage === index + 1 ? 'active' : ''}`} onClick={() => setCurrentPage(index + 1)}>
                {index + 1}
              </button>
            ))}
            <button className="page-btn" onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))} disabled={currentPage === totalPages}>Next</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Statements;